import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useUser } from '@/domain/user/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { favouritesAPIClient } from '@/scopes/favourites/infrastructure/api'
import { createFavoriteClickedEvent, FavoriteClickedEventProps } from '@/scopes/search/search/domain/analytics'

export function useFavouriteProductIdsQuery() {
  return useQuery({
    queryKey: ['favourite-products'],
    queryFn: () => favouritesAPIClient.getFavouriteProducts(),
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  })
}

export function useFavouriteProducts(contextItemId: string) {
  const queryClient = useQueryClient()
  const { isLoading, isFetching, data: favouriteProductIds } = useFavouriteProductIdsQuery()

  const invalidateFavouriteProducts = () => {
    queryClient.invalidateQueries({ queryKey: ['favourite-products'] })
  }

  const addProductToFavouritesMutation = useMutation({
    mutationFn: (productId: string) =>
      favouritesAPIClient.addProductToFavourites({
        contextItemId,
        productId,
      }),
    onSuccess: invalidateFavouriteProducts,
  })

  const addFavouriteState = React.useCallback(
    (productId: string) => {
      addProductToFavouritesMutation.mutate(productId)
    },
    [addProductToFavouritesMutation],
  )

  return {
    isLoading,
    isFetching,
    favouriteProductIds,
    addFavouriteState,
    invalidateFavouriteProducts,
  }
}

export function useAddFavoriteFromUrl(contextItemId: string) {
  const { isLoggedIn } = useUser()
  const { addFavouriteState } = useFavouriteProducts(contextItemId)
  const [addFavoriteParam, setAddFavoriteParam] = React.useState('')

  React.useEffect(() => {
    const url = window.location.search
    const params = new URLSearchParams(url.toLowerCase())
    const addFavoriteParam = params.get('addfavorite')

    if (addFavoriteParam) {
      setAddFavoriteParam(addFavoriteParam)
    }
  }, [])

  React.useEffect(() => {
    if (isLoggedIn && addFavoriteParam) {
      addFavouriteState(addFavoriteParam)
    }
  }, [isLoggedIn])
}

export function useFavouriteProduct(productId: number | null, contextItemId: string) {
  const { isLoading, isFetching, favouriteProductIds, invalidateFavouriteProducts }
    = useFavouriteProducts(contextItemId)
  const { sendEvent } = useAnalytics()
  const strProductId = productId === null ? '' : productId.toString()

  const isFavourite = favouriteProductIds?.includes(strProductId) ?? false

  const addProductToFavouritesMutation = useMutation({
    mutationFn: () =>
      favouritesAPIClient.addProductToFavourites({
        contextItemId,
        productId: strProductId,
      }),
    onSuccess: invalidateFavouriteProducts,
  })

  const removeProductFromFavouritesMutation = useMutation({
    mutationFn: () =>
      favouritesAPIClient.removeProductFromFavourites({
        contextItemId,
        productId: strProductId,
      }),
    onSuccess: invalidateFavouriteProducts,
  })

  const toggleFavouriteState = React.useCallback(
    ({
      occupancyInfo,
      id,
      name,
      cityId,
      countryId,
      regionId,
      countryName,
      regionName,
      cityName,
      countryEnglishName,
      regionEnglishName,
      cityEnglishName,
      collection,
      rating,
      userRating,
      price,
      campaign,
      featuredFilters,
      departureDateDetail,
      origin,
    }: FavoriteClickedEventProps) => {
      const event = {
        occupancyInfo,
        id,
        name,
        cityId,
        countryId,
        regionId,
        countryName,
        regionName,
        cityName,
        countryEnglishName,
        regionEnglishName,
        cityEnglishName,
        collection,
        rating,
        userRating,
        price,
        campaign,
        featuredFilters,
        departureDateDetail,
        origin,
      }

      const mutation = isFavourite
        ? removeProductFromFavouritesMutation
        : addProductToFavouritesMutation
      const actionResult = isFavourite
        ? 'not_marked_as_favorite'
        : 'marked_as_favorite'
      mutation.mutate()
      sendEvent(createFavoriteClickedEvent({ ...event, actionResult }))
    },
    [addProductToFavouritesMutation, removeProductFromFavouritesMutation, isFavourite],
  )

  return {
    isLoading,
    isFetching,
    isFavourite,
    toggleFavouriteState,
  }
}
